import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AI_MODELS, PROMPT_TEMPLATES, IMAGE_STYLES, GENERATED_IMAGES,
  generateUsageStats, getSimulatedResponse, loadAIState, saveAIState,
} from './aiData';
import AIChat from './AIChat';
import './AIDashboard.css';

function AIDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chat');
  const [state, setState] = useState(loadAIState);
  const [selectedModel, setSelectedModel] = useState('claude');
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageStyle, setImageStyle] = useState('realistic');
  const [isGenerating, setIsGenerating] = useState(false);

  const stats = generateUsageStats();

  const update = useCallback((newState) => {
    setState(newState);
    saveAIState(newState);
  }, []);

  const handleSendMessage = useCallback((text) => {
    const userMsg = { id: Date.now().toString(), role: 'user', content: text, timestamp: Date.now() };

    setState((prev) => {
      const convs = prev.conversations.map((c) =>
        c.id === prev.activeConvId
          ? { ...c, messages: [...c.messages, userMsg], title: c.messages.length === 0 ? text.slice(0, 40) : c.title }
          : c
      );
      const ns = { ...prev, conversations: convs };
      saveAIState(ns);
      return ns;
    });

    // Simulate AI response
    setTimeout(() => {
      const aiMsg = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: getSimulatedResponse(),
        timestamp: Date.now(),
        model: selectedModel,
        tokens: Math.round(200 + Math.random() * 800),
        latency: (0.8 + Math.random() * 2).toFixed(1),
      };

      setState((prev) => {
        const convs = prev.conversations.map((c) =>
          c.id === prev.activeConvId ? { ...c, messages: [...c.messages, aiMsg] } : c
        );
        const ns = { ...prev, conversations: convs };
        saveAIState(ns);
        return ns;
      });
    }, 1500 + Math.random() * 2000);
  }, [selectedModel]);

  const handleNewChat = () => {
    const newConv = { id: Date.now().toString(), title: 'New Chat', model: selectedModel, messages: [], createdAt: Date.now() };
    const ns = { conversations: [newConv, ...state.conversations], activeConvId: newConv.id };
    update(ns);
  };

  const handleSelectConv = (id) => {
    update({ ...state, activeConvId: id });
  };

  const handleDeleteConv = (id) => {
    const convs = state.conversations.filter((c) => c.id !== id);
    if (convs.length === 0) {
      const newConv = { id: Date.now().toString(), title: 'New Chat', model: selectedModel, messages: [], createdAt: Date.now() };
      convs.push(newConv);
    }
    const activeId = id === state.activeConvId ? convs[0].id : state.activeConvId;
    update({ conversations: convs, activeConvId: activeId });
  };

  const handleUseTemplate = (template) => {
    setActiveTab('chat');
    handleSendMessage(template.prompt);
  };

  const handleGenerateImage = () => {
    if (!imagePrompt.trim()) return;
    setIsGenerating(true);
    setTimeout(() => setIsGenerating(false), 3000);
  };

  const handleReset = () => {
    localStorage.removeItem('demohub_ai');
    setState(loadAIState());
  };

  const activeConv = state.conversations.find((c) => c.id === state.activeConvId) || state.conversations[0];
  const modelInfo = AI_MODELS.find((m) => m.id === selectedModel);
  const tokenPct = (stats.tokensUsed / stats.tokensLimit) * 100;

  return (
    <div className="ai-app">
      <div className="ai-topbar">
        <div className="ai-topbar-left">
          <button className="ai-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ai-title">AI Studio</h1>
          <span className="ai-model-badge" style={{ background: modelInfo.color + '22', color: modelInfo.color }}>
            {modelInfo.icon} {modelInfo.name}
          </span>
        </div>
        <div className="ai-topbar-right">
          <div className="ai-tabs">
            {[
              { id: 'chat', label: 'Chat', icon: '💬' },
              { id: 'images', label: 'Images', icon: '🎨' },
              { id: 'templates', label: 'Templates', icon: '📋' },
              { id: 'models', label: 'Models', icon: '🧠' },
              { id: 'usage', label: 'Usage', icon: '📊' },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`ai-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="ai-tab-icon">{tab.icon}</span>
                <span className="ai-tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
          <button className="ai-reset-btn" onClick={handleReset}>↻ Reset</button>
        </div>
      </div>

      <div className="ai-content">
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <AIChat
            conversations={state.conversations}
            activeConv={activeConv}
            selectedModel={selectedModel}
            models={AI_MODELS}
            onSend={handleSendMessage}
            onNewChat={handleNewChat}
            onSelectConv={handleSelectConv}
            onDeleteConv={handleDeleteConv}
            onChangeModel={setSelectedModel}
          />
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="ai-images-tab">
            <div className="ai-image-gen">
              <h2>Image Generation</h2>
              <p className="ai-subtitle">Describe what you want to create</p>
              <div className="ai-image-prompt-area">
                <textarea
                  placeholder="A futuristic city skyline at golden hour with neon lights reflecting on glass buildings..."
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  rows={3}
                />
                <div className="ai-image-styles">
                  {IMAGE_STYLES.map((style) => (
                    <button
                      key={style.id}
                      className={`ai-style-btn ${imageStyle === style.id ? 'active' : ''}`}
                      onClick={() => setImageStyle(style.id)}
                    >
                      <span>{style.icon}</span>
                      <span>{style.name}</span>
                    </button>
                  ))}
                </div>
                <button
                  className={`ai-generate-btn ${imagePrompt.trim() ? 'active' : ''}`}
                  onClick={handleGenerateImage}
                  disabled={!imagePrompt.trim() || isGenerating}
                >
                  {isGenerating ? (
                    <><span className="ai-spinner"></span> Generating...</>
                  ) : (
                    '✨ Generate Image'
                  )}
                </button>
              </div>
            </div>
            <div className="ai-image-gallery">
              <h3>Recent Generations</h3>
              <div className="ai-image-grid">
                {GENERATED_IMAGES.map((img) => (
                  <div key={img.id} className="ai-image-card" style={{ background: img.color + '15' }}>
                    <div className="ai-image-preview">
                      <span className="ai-image-emoji">{img.emoji}</span>
                    </div>
                    <div className="ai-image-info">
                      <span className="ai-image-prompt-text">{img.prompt}</span>
                      <span className="ai-image-style-tag">{img.style}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="ai-templates-tab">
            <h2>Prompt Templates</h2>
            <p className="ai-subtitle">Quick-start with pre-built prompts for common tasks</p>
            <div className="ai-templates-grid">
              {PROMPT_TEMPLATES.map((tmpl) => (
                <div key={tmpl.id} className="ai-template-card">
                  <div className="ai-template-header">
                    <span className="ai-template-icon">{tmpl.icon}</span>
                    <span className="ai-template-category">{tmpl.category}</span>
                  </div>
                  <h3>{tmpl.title}</h3>
                  <p className="ai-template-prompt">{tmpl.prompt.slice(0, 100)}...</p>
                  <button className="ai-template-use" onClick={() => handleUseTemplate(tmpl)}>
                    Use Template →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Models Tab */}
        {activeTab === 'models' && (
          <div className="ai-models-tab">
            <h2>AI Models</h2>
            <p className="ai-subtitle">Compare and select the best model for your task</p>
            <div className="ai-models-grid">
              {AI_MODELS.map((model) => (
                <div
                  key={model.id}
                  className={`ai-model-card ${selectedModel === model.id ? 'selected' : ''}`}
                  onClick={() => setSelectedModel(model.id)}
                >
                  <div className="ai-model-header">
                    <span className="ai-model-icon">{model.icon}</span>
                    <span className="ai-model-provider">{model.provider}</span>
                    {selectedModel === model.id && <span className="ai-model-active">Active</span>}
                  </div>
                  <h3 className="ai-model-name">{model.name}</h3>
                  <div className="ai-model-specs">
                    <div className="ai-model-spec">
                      <span className="ai-spec-label">Speed</span>
                      <span className="ai-spec-value">{model.speed}</span>
                    </div>
                    <div className="ai-model-spec">
                      <span className="ai-spec-label">Quality</span>
                      <span className="ai-spec-value">{model.quality}</span>
                    </div>
                    <div className="ai-model-spec">
                      <span className="ai-spec-label">Context</span>
                      <span className="ai-spec-value">{model.context}</span>
                    </div>
                    <div className="ai-model-spec">
                      <span className="ai-spec-label">Cost/1K</span>
                      <span className="ai-spec-value">{model.costPer1k}</span>
                    </div>
                  </div>
                  <button
                    className={`ai-model-select-btn ${selectedModel === model.id ? 'active' : ''}`}
                    style={selectedModel === model.id ? { background: model.color, borderColor: model.color } : {}}
                  >
                    {selectedModel === model.id ? '✓ Selected' : 'Select Model'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="ai-usage-tab">
            <h2>Usage & Billing</h2>
            <p className="ai-subtitle">Monitor your API usage and costs</p>

            <div className="ai-usage-kpis">
              <div className="ai-usage-kpi">
                <span className="ai-usage-kpi-icon">🔢</span>
                <div className="ai-usage-kpi-info">
                  <span className="ai-usage-kpi-value">{(stats.tokensUsed / 1000).toFixed(0)}K</span>
                  <span className="ai-usage-kpi-label">Tokens Used</span>
                </div>
                <div className="ai-usage-bar">
                  <div className="ai-usage-fill" style={{ width: `${tokenPct}%` }}></div>
                </div>
                <span className="ai-usage-limit">{tokenPct.toFixed(0)}% of {(stats.tokensLimit / 1000).toFixed(0)}K limit</span>
              </div>
              <div className="ai-usage-kpi">
                <span className="ai-usage-kpi-icon">📡</span>
                <div className="ai-usage-kpi-info">
                  <span className="ai-usage-kpi-value">{stats.apiCalls.toLocaleString()}</span>
                  <span className="ai-usage-kpi-label">API Calls</span>
                </div>
              </div>
              <div className="ai-usage-kpi">
                <span className="ai-usage-kpi-icon">⚡</span>
                <div className="ai-usage-kpi-info">
                  <span className="ai-usage-kpi-value">{stats.avgLatency}s</span>
                  <span className="ai-usage-kpi-label">Avg Latency</span>
                </div>
              </div>
              <div className="ai-usage-kpi">
                <span className="ai-usage-kpi-icon">💰</span>
                <div className="ai-usage-kpi-info">
                  <span className="ai-usage-kpi-value">${stats.costThisMonth.toFixed(2)}</span>
                  <span className="ai-usage-kpi-label">Cost This Month</span>
                </div>
                <span className={`ai-usage-change ${stats.costThisMonth > stats.costLastMonth ? 'up' : 'down'}`}>
                  {stats.costThisMonth > stats.costLastMonth ? '↑' : '↓'} ${Math.abs(stats.costThisMonth - stats.costLastMonth).toFixed(2)} vs last month
                </span>
              </div>
            </div>

            <div className="ai-usage-chart-card">
              <h3>Daily Token Usage (14 days)</h3>
              <div className="ai-usage-chart">
                {stats.dailyUsage.map((d, i) => (
                  <div key={i} className="ai-usage-chart-col" title={`${d.day}: ${d.tokens.toLocaleString()} tokens`}>
                    <div
                      className="ai-usage-chart-bar"
                      style={{ height: `${(d.tokens / 120000) * 100}%` }}
                    ></div>
                    <span className="ai-usage-chart-label">{d.day.split(' ')[1]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIDashboard;
